// ============= Imports =============

import { tokenManager } from '@tw/tw-component-library';
import { PaymentWorkflowType } from '../../../components/container/forms/types/api/graph.types';
import {
  AssignedWorkflowAnalytics,
  AssignedWorkflowData,
} from '../../../types/analytics.types';
import { AuthContextProps } from '../../../contexts/AuthContext';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/SectionAssignment/me/active';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const HEADERS = {};

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

/**
 * Processes assigned workflow data and calculates analytics
 * @param assignments Array of workflow assignment data
 * @returns Processed analytics data
 */
const processAssignedWorkflowData = (
  assignments: AssignedWorkflowData[]
): AssignedWorkflowAnalytics => {
  // Count by assignment type (Individual, Group, etc.)
  const statusCounts: Record<string, number> = {};

  assignments.forEach((assignment) => {
    const status = assignment.assignmentType;
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  // Convert to array format
  const byStatus = Object.entries(statusCounts).map(([status, count]) => ({
    status,
    count,
  }));

  // Transform assignment data to match AssignedWorkflowData interface
  const transformedWorkflows: AssignedWorkflowData[] = assignments.map(
    (assignment) => ({
      assignmentId: assignment.assignmentId,
      assignmentType: assignment.assignmentType,
      createdAt: assignment.createdAt,
      createdBy: assignment.createdBy,
      displayName: assignment.displayName,
      email: assignment.email,
      id: assignment.id,
      isActive: assignment.isActive,
      order: assignment.order,
      sectionName: assignment.sectionName,
      workflowId: assignment.workflowId,
      workflowName: assignment.workflowName,
      workflowSectionId: assignment.workflowSectionId,
      workflowType: assignment.workflowType,
      workflowStatus: assignment.workflowStatus,
    })
  );

  return {
    totalAssigned: assignments.length,
    byStatus,
    workflows: transformedWorkflows,
  };
};

// ============= Main Function =============
export interface GetAssignedWorkflowsParams {
  searchTerm?: string;
  status?: string;
  workflowType?: PaymentWorkflowType;
  sortDescending?: boolean;
  sortColumn?: string;
}

/**
 * Fetches currently assigned workflow sections from Microsoft API
 * @param getAccessToken Function to acquire access token
 * @param spoofedUser Spoofed user email for testing
 * @returns Promise with API response containing assigned workflow analytics or error
 */
export const getAssignedWorkflows = async (
  getAccessToken: AuthContextProps['getAccessToken'],
  spoofedUser: string | null
) => {
  try {
    console.log('🔄 Fetching assigned workflow sections data...');
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    const url = `${BASE_URL}${API_ENDPOINT}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token, spoofedUser || null),
    });

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('📊 Raw API response:', data);

    // The API now returns an array of workflow assignments directly
    const assignments: AssignedWorkflowData[] = Array.isArray(data)
      ? data
      : data.items || [];

    // Format dates for display
    const formattedAssignments: AssignedWorkflowData[] = assignments.map(
      (assignment: AssignedWorkflowData) => ({
        ...assignment,
        createdAt: assignment.createdAt
          ? new Date(assignment.createdAt).toLocaleDateString()
          : assignment.createdAt,
      })
    );

    // Process the data to create analytics
    const analytics = processAssignedWorkflowData(formattedAssignments);

    console.log('✅ Assigned workflow sections data fetched successfully');

    return {
      success: true,
      data: analytics,
    };
  } catch (error) {
    console.error('🔴 Error fetching assigned workflow sections data:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
